import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import ContactApiService from "../services/ContactApiService";

const MAX_IMPORT_ROWS = 1000;
const BULK_CHUNK_SIZE = 500;
const MAX_PHONE_DIGITS = 15;
const MIN_PHONE_DIGITS_FOR_IMPORT = 11;

const digitsToPhonePayload = (digitsFromFile) => {
  const d = String(digitsFromFile || "").replace(/\D/g, "");
  return `+${d}`;
};

const normalizeScientificPhone = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const sciPattern = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/;
  if (sciPattern.test(raw)) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed.toFixed(0);
  }
  return raw;
};

/** Digits-only string suitable for dup key comparison (aligned with CRM / last 10 rule). */
const duplicateKeyFromDigits = (digits) => {
  const d = String(digits || "").replace(/\D/g, "");
  if (!d.length) return "";
  if (d.length > 10) return d.slice(-10);
  return d;
};

const getDigitsOnly = (phoneRaw) =>
  normalizeScientificPhone(String(phoneRaw ?? "")).replace(/\D/g, "");

const describePhoneIssue = (nameTrim, digits) => {
  if (!nameTrim) return "Name is required";
  if (!digits.length) return "Phone number is required";
  if (digits.length < 10)
    return `Invalid phone (${digits.length} digits) — check spaces or incomplete number`;
  if (digits.length === 10)
    return "Indian-style 10-digit number without country code — add country prefix (e.g. 91)";
  if (digits.length > MAX_PHONE_DIGITS)
    return `Too many digits (${digits.length}) — max ${MAX_PHONE_DIGITS}`;
  return "";
};

/** Valid for auto-import path: name present, canonical length acceptable for outbound. */
const isAutoReady = (nameTrim, digits) => {
  if (!nameTrim) return false;
  if (digits.length < MIN_PHONE_DIGITS_FOR_IMPORT) return false;
  if (digits.length > MAX_PHONE_DIGITS) return false;
  return true;
};

/**
 * Ordered pass: duplicates with same canonical key skipped (first occurrence kept only when auto-ready later).
 */
const partitionImportRows = (contacts) => {
  const occupiedKeys = new Set();
  let duplicatesSkipped = 0;
  const validContacts = [];
  const needsReview = [];

  for (const contact of contacts) {
    const nameTrim = String(contact.name || "").trim();
    const phoneRaw = contact.phone ?? "";
    const digits = getDigitsOnly(phoneRaw);

    const rowEmpty = !nameTrim && !digits.length;

    if (rowEmpty) continue;

    if (isAutoReady(nameTrim, digits)) {
      const key = duplicateKeyFromDigits(digits);
      if (occupiedKeys.has(key)) {
        duplicatesSkipped += 1;
        continue;
      }
      occupiedKeys.add(key);
      validContacts.push({
        name: nameTrim,
        phone: digits,
        row: contact.row,
      });
      continue;
    }

    needsReview.push({
      id: `review-${contact.row}-${needsReview.length}`,
      row: contact.row,
      name: nameTrim,
      phone: normalizeScientificPhone(phoneRaw),
      issue: describePhoneIssue(nameTrim, digits),
    });
  }

  return { validContacts, needsReview, duplicatesSkipped };
};

/**
 * Dedupe merged list (first occurrence wins).
 */
function dedupeImportedRows(rows) {
  const seen = new Set();
  const out = [];
  let duplicatesSkipped = 0;
  for (const r of rows) {
    const d = typeof r.phone === "string" ? r.phone.replace(/\D/g, "") : String(r.phone).replace(/\D/g, "");
    const key = duplicateKeyFromDigits(d);
    if (!key || d.length < MIN_PHONE_DIGITS_FOR_IMPORT) continue;
    if (seen.has(key)) {
      duplicatesSkipped += 1;
      continue;
    }
    seen.add(key);
    out.push({
      ...r,
      name: String(r.name || "").trim(),
      phone: d,
    });
  }
  return { rows: out, duplicatesSkipped };
}

const ContactImportDialog = ({
  open,
  onClose,
  onImport,
  existingContacts: _ignoredExistingContacts = [],
  loadingExistingContacts = false,
  /** Shown under the title when importing into a group context */
  groupHint = null,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [allValidContacts, setAllValidContacts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [structuralError, setStructuralError] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [duplicatesSkipped, setDuplicatesSkipped] = useState(0);

  /** Rows still unresolved (blocked import until fixed or discarded via apply). */
  const [needsReviewRows, setNeedsReviewRows] = useState([]);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixRows, setFixRows] = useState([]);
  const [fixValidateById, setFixValidateById] = useState({});

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result.map((v) => v.replace(/^"|"$/g, ""));
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter((l) => l.trim());
    if (lines.length === 0) {
      return { error: "CSV file is empty", contacts: [] };
    }
    const headers = parseCSVLine(lines[0]);
    const nameIdx = headers.findIndex((h) => h.toLowerCase().includes("name"));
    const phoneIdx = headers.findIndex((h) => h.toLowerCase().includes("phone"));

    if (nameIdx === -1 || phoneIdx === -1) {
      const missingColumns = [];
      if (nameIdx === -1) missingColumns.push("'Name'");
      if (phoneIdx === -1) missingColumns.push("'Phonenumber'");
      return {
        error: `Required column ${missingColumns.join(" and ")} not found.`,
        contacts: [],
      };
    }

    const contacts = lines.slice(1).map((line, idx) => {
      const values = parseCSVLine(line);
      return {
        row: idx + 2,
        name: values[nameIdx] || "",
        phone: values[phoneIdx] || "",
      };
    });

    return { error: null, contacts };
  };

  const parseXLSX = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return { error: "Excel file is empty", contacts: [] };
    }

    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

    if (!rows.length) {
      return { error: "Excel file is empty", contacts: [] };
    }

    const headers = (rows[0] || []).map((h) => String(h || "").trim());
    const nameIdx = headers.findIndex((h) => h.toLowerCase().includes("name"));
    const phoneIdx = headers.findIndex((h) => h.toLowerCase().includes("phone"));

    if (nameIdx === -1 || phoneIdx === -1) {
      const missingColumns = [];
      if (nameIdx === -1) missingColumns.push("'Name'");
      if (phoneIdx === -1) missingColumns.push("'Phonenumber'");
      return {
        error: `Required column ${missingColumns.join(" and ")} not found.`,
        contacts: [],
      };
    }

    const contacts = rows
      .slice(1)
      .filter((row) => row && row.some((cell) => String(cell || "").trim() !== ""))
      .map((row, idx) => ({
        row: idx + 2,
        name: String(row[nameIdx] || "").trim(),
        phone: String(row[phoneIdx] || "").trim(),
      }));

    return { error: null, contacts };
  };

  const handleFileUpload = (event) => {
    if (loadingExistingContacts) return;
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setFileName(file.name);
    setStructuralError(null);
    setDuplicatesSkipped(0);
    setNeedsReviewRows([]);
    setFixValidateById({});

    const processParsedContacts = (error, contacts) => {
      if (error) {
        setStructuralError(error);
        setAllValidContacts([]);
        return;
      }

      if (contacts.length === 0) {
        setStructuralError("No valid data found in file");
        setAllValidContacts([]);
        return;
      }

      if (contacts.length > MAX_IMPORT_ROWS) {
        setStructuralError(
          `Maximum ${MAX_IMPORT_ROWS} contacts per import. Your file has ${contacts.length} rows.`,
        );
        setAllValidContacts([]);
        return;
      }

      const { validContacts, needsReview, duplicatesSkipped: dupCt } =
        partitionImportRows(contacts);
      setAllValidContacts(validContacts);
      setNeedsReviewRows(needsReview);
      setDuplicatesSkipped(dupCt);
      setStructuralError(null);

      if (needsReview.length > 0) {
        setFixRows(JSON.parse(JSON.stringify(needsReview)));
        setFixDialogOpen(true);
      }
    };

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const reader = new FileReader();

    if (fileExtension === "xlsx") {
      reader.onload = (e) => {
        const { error, contacts } = parseXLSX(e.target.result);
        processParsedContacts(error, contacts);
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (e) => {
        const { error, contacts } = parseCSV(e.target.result);
        processParsedContacts(error, contacts);
      };
      reader.readAsText(file, "UTF-8");
    }

    event.target.value = "";
  };

  const openFixDialog = () => {
    setFixRows(JSON.parse(JSON.stringify(needsReviewRows)));
    setFixValidateById({});
    setFixDialogOpen(true);
  };

  const updateFixRow = (id, field, value) => {
    setFixRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      ));
    setFixValidateById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  /** Validate fix dialog rows; merge into import list with global dedupe (first wins). */
  const applyFixDialog = () => {
    const nextErrors = {};
    const fixedValidated = [];

    for (const r of fixRows) {
      const nameTrim = String(r.name || "").trim();
      const digits = getDigitsOnly(r.phone);
      let err = "";

      if (!nameTrim) err = "Name is required";
      else if (!digits.length) err = "Phone number is required";
      else if (digits.length < MIN_PHONE_DIGITS_FOR_IMPORT) {
        err = `Include country code (at least ${MIN_PHONE_DIGITS_FOR_IMPORT} digits total)`;
      } else if (digits.length > MAX_PHONE_DIGITS) {
        err = `At most ${MAX_PHONE_DIGITS} digits`;
      }

      if (err) nextErrors[r.id] = err;
      else fixedValidated.push({ name: nameTrim, phone: digits, row: r.row });
    }

    setFixValidateById(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const sanitizedValid = allValidContacts.map((c) => ({
      name: String(c.name || "").trim(),
      phone: String(c.phone || "").replace(/\D/g, ""),
    }));

    const combined = [...sanitizedValid, ...fixedValidated.map((x) => ({ name: x.name, phone: x.phone }))];
    const { rows: merged, duplicatesSkipped: dupAfterFix } = dedupeImportedRows(
      combined.map((x) => ({ name: x.name, phone: x.phone })),
    );

    setDuplicatesSkipped((prev) => prev + dupAfterFix);
    setAllValidContacts(merged.map(({ name, phone }) => ({ name, phone })));
    setNeedsReviewRows([]);
    setFixDialogOpen(false);
    setFixRows([]);
    setFixValidateById({});
  };

  const closeFixDialogWithoutApply = () => {
    setFixDialogOpen(false);
  };

  const importChunkSequential = async (chunk, offsetInAll) => {
    let ok = 0;
    let bad = 0;
    const ids = [];
    for (let i = 0; i < chunk.length; i++) {
      const contact = chunk[i];
      setImportProgress({
        current: offsetInAll + i + 1,
        total: allValidContacts.length,
      });
      try {
        const result = await onImport({
          name: contact.name,
          phone: contact.phone,
        });
        if (result?.success) {
          ok++;
          if (result.contactId) ids.push(result.contactId);
        } else bad++;
      } catch (e) {
        bad++;
      }
    }
    return { ok, bad, ids };
  };

  const handleImport = async () => {
    setImporting(true);
    let success = 0;
    let failed = 0;
    let createdCount = 0;
    let updatedCount = 0;
    const importedContactIds = [];

    const rows = allValidContacts.map((c) => ({
      name: c.name.trim(),
      phone_number: digitsToPhonePayload(c.phone),
    }));

    const chunks = [];
    for (let i = 0; i < rows.length; i += BULK_CHUNK_SIZE) {
      chunks.push(rows.slice(i, i + BULK_CHUNK_SIZE));
    }

    let offset = 0;
    for (let b = 0; b < chunks.length; b++) {
      setImportProgress({ current: b + 1, total: chunks.length });
      const res = await ContactApiService.bulkImportContacts(chunks[b]);
      if (res.success) {
        success += res.imported_count;
        failed += res.failed_count;
        createdCount += res.created_count || 0;
        updatedCount += res.updated_count || 0;
        for (const r of res.results || []) {
          if (r.id) importedContactIds.push(r.id);
        }
      } else {
        const slice = allValidContacts.slice(offset, offset + chunks[b].length);
        const seq = await importChunkSequential(slice, offset);
        success += seq.ok;
        failed += seq.bad;
        importedContactIds.push(...seq.ids);
      }
      offset += chunks[b].length;
    }

    setImporting(false);
    setSelectedFile(null);
    setFileName("");
    setAllValidContacts([]);
    setStructuralError(null);
    setDuplicatesSkipped(0);
    setNeedsReviewRows([]);
    setFixDialogOpen(false);
    setFixRows([]);
    setFixValidateById({});
    setImportProgress({ current: 0, total: 0 });

    onClose(success, failed, { importedContactIds, createdCount, updatedCount });
  };

  const downloadSample = () => {
    const csv = "Name,PhoneNumber\nPerson1,+919876543210\nPerson2,+919876543211\nPerson3,+919876543212";
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sample_contacts.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const resetDialog = () => {
    setSelectedFile(null);
    setFileName("");
    setAllValidContacts([]);
    setStructuralError(null);
    setDuplicatesSkipped(0);
    setNeedsReviewRows([]);
    setFixDialogOpen(false);
    setFixRows([]);
    setFixValidateById({});
    setImporting(false);
    setImportProgress({ current: 0, total: 0 });
  };

  const handleClose = () => {
    resetDialog();
    onClose(0, 0, { importedContactIds: [] });
  };

  const structuralOk = !structuralError;
  const blockingReview = needsReviewRows.length > 0;
  const canImport = structuralOk && !blockingReview && allValidContacts.length > 0;

  void _ignoredExistingContacts;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
              <Box display="flex" alignItems="center" gap={1}>
                <UploadIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Import Contacts</Typography>
              </Box>
              {groupHint ? (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 4, maxWidth: 360 }}>
                  {groupHint}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ bgcolor: "#f8fafc", p: 2, borderRadius: 2, mb: 3 }}>
            <Button size="small" onClick={downloadSample} startIcon={<DownloadIcon />}>
              Download Sample CSV
            </Button>
          </Box>

          {!importing ? (
            !selectedFile ? (
              <Box
                onClick={() => {
                  if (!loadingExistingContacts) {
                    document.getElementById("csv-input").click();
                  }
                }}
                sx={{
                  border: "2px dashed #cbd5e1",
                  borderRadius: 2,
                  p: 2,
                  width: 300,
                  mx: "auto",
                  textAlign: "center",
                  cursor: loadingExistingContacts ? "not-allowed" : "pointer",
                  opacity: loadingExistingContacts ? 0.6 : 1,
                  "&:hover": loadingExistingContacts
                    ? {}
                    : { borderColor: "#1976d2", bgcolor: "#f0f7ff" },
                }}
              >
                <input
                  id="csv-input"
                  type="file"
                  accept=".csv,.xlsx"
                  hidden
                  onChange={handleFileUpload}
                />
                <UploadIcon sx={{ fontSize: 30, color: "#94a3b8", mb: 1 }} />
                <Typography variant="body2">Click to upload CSV or XLSX file</Typography>
                <Typography variant="caption" color="text.secondary">
                  {loadingExistingContacts
                    ? "Preparing duplicate checks..."
                    : "Only .csv and .xlsx files accepted"}
                </Typography>
                {loadingExistingContacts && (
                  <LinearProgress sx={{ mt: 1.5, borderRadius: 1, height: 6 }} />
                )}
              </Box>
            ) : (
              <>
                <Alert severity={structuralError ? "error" : "success"} sx={{ mb: 2 }}>
                  {structuralError ? (
                    <Typography variant="body2" color="error">
                      {structuralError}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="subtitle2" fontWeight={600}>{fileName}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Ready to import: <strong>{allValidContacts.length}</strong> contact(s)
                      </Typography>
                      {duplicatesSkipped > 0 && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Duplicate rows in file skipped (first kept): <strong>{duplicatesSkipped}</strong>
                        </Typography>
                      )}
                    </>
                  )}
                </Alert>

                {!structuralError && blockingReview && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>{needsReviewRows.length}</strong> row(s) need name or phone fixes before import.
                    </Typography>
                    <Button size="small" sx={{ mt: 1 }} variant="outlined" startIcon={<EditIcon />} onClick={openFixDialog}>
                      Review & edit
                    </Button>
                  </Alert>
                )}

                {!structuralError && !blockingReview && duplicatesSkipped > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Duplicate numbers in the file were merged — only the first occurrence per number is imported.
                  </Alert>
                )}

                <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <Button size="small" variant="text" onClick={() => { resetDialog(); }}>
                    Choose another file
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={!canImport}
                  >
                    Import {canImport ? allValidContacts.length : 0} Contacts
                  </Button>
                </Box>
              </>
            )
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" fontWeight={500}>
                Importing Contacts...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {importProgress.total > 0
                  ? `Batch ${importProgress.current} of ${importProgress.total}`
                  : ""}
              </Typography>
              <LinearProgress
                sx={{ mt: 2, borderRadius: 1, height: 8 }}
                variant="determinate"
                value={
                  importProgress.total > 0
                    ? (importProgress.current / importProgress.total) * 100
                    : 0
                }
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={handleClose} disabled={importing}>
            {importing ? "Importing..." : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fixDialogOpen}
        onClose={closeFixDialogWithoutApply}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={600}>Fix contacts</Typography>
              <Typography variant="body2" color="text.secondary">
                Edit name and phone here (spaces are removed). Include country code — at least {MIN_PHONE_DIGITS_FOR_IMPORT} digits.
              </Typography>
            </Box>
            <IconButton onClick={closeFixDialogWithoutApply} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={72}>Row</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fixRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.row}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.issue} color="warning" variant="outlined" sx={{ maxWidth: 220, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={r.name}
                        onChange={(e) => updateFixRow(r.id, "name", e.target.value)}
                        error={Boolean(fixValidateById[r.id])}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={r.phone}
                        onChange={(e) => updateFixRow(r.id, "phone", e.target.value)}
                        error={Boolean(fixValidateById[r.id])}
                        helperText={fixValidateById[r.id] ?? ""}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={closeFixDialogWithoutApply}>Close</Button>
          <Button variant="contained" onClick={applyFixDialog}>
            Apply fixes & continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactImportDialog;
